import * as React from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { filter, find, includes, toLower } from "lodash";
import { Flex } from "@rebass/grid";
import { Intent } from "@blueprintjs/core";

import { Dimension, Output, CalculationBlock } from "../../../../types/models";
import { ThunkDispatch } from "../../../../types/redux";
import { RootState } from "../../../../store/reducer";
import { getDimensions } from "../../../../store/modules/dimensions";
import {
  getOutputs,
  saveOutput,
  deleteOutput,
} from "../../../../store/modules/outputs";
import {
  CalculationBlockTypes,
  getCalculationBlocks,
  saveCalculationBlock,
} from "../../../../store/modules/calculation_blocks";

import AppViewTemplate from "../../../templates/AppView";
import Loading from "../../../molecules/Loading";
import InputOutputEditorTabs from "../../../organisms/InputOutputEditorTabs";
import TabBar from "../../../organisms/TabBar";
import OutputsTable from "../../../organisms/OutputsTable";
import EditOutputModal from "../../../organisms/EditOutputModal";
import { MaxBound } from "../../../atoms/Layout";
import { getFormatParameters } from "../../../../store/modules/format_parameter";
import FloatingButton from "../../../atoms/FloatingButton";
import { selectOutputRow } from "../../../../store/modules/selected_output_row";
import api, { HTTPError } from "../../../../services/api";
import { ClientMap } from "src/store/modules/client";
import Toaster from "../../../molecules/Toaster";

interface IProps extends RouteComponentProps<any> {
  dimensions: Dimension[];
  dispatch: ThunkDispatch;
  outputs: Output[];
  calculationBlocks: CalculationBlock[];
  clients: ClientMap;
}

interface IState {
  isLoading: boolean;
  isEditing: boolean;
  clientId: number;
  modelId: number;
  modelInstanceId: number;
  editedOutput?: Output;
  outputFilter: string;
  outputsList: Output[];
  outputTags: any[];
  isClientTaxonomyEnabled: boolean;
}

class OutputEditorView extends React.Component<IProps, IState> {
  public state: IState = {
    isLoading: true,
    isEditing: false,
    clientId: this.props.match.params.client,
    modelId: this.props.match.params.model,
    modelInstanceId: this.props.match.params.instance,
    outputFilter: "",
    outputsList: [],
    outputTags: [],
    isClientTaxonomyEnabled: false
  };

  public componentDidMount() {
    const { dispatch } = this.props;
    const { modelInstanceId, clientId } = this.state;
    const isClientTaxonomyEnabled = this.props.clients[clientId].IsClientTaxonomyEnabled;
    this.setState({ isClientTaxonomyEnabled });
    Promise.all([
      dispatch(getFormatParameters(modelInstanceId)),
      dispatch(getDimensions(modelInstanceId)),
      dispatch(getOutputs(modelInstanceId)),
      dispatch(getCalculationBlocks(modelInstanceId)),
      this.fetchOutputTags(clientId),
    ]).then(() =>
      this.setState({ isLoading: false, outputsList: this.props.outputs })
    );
  }

  private fetchOutputTags = (clientId: number) => {
    return api
      .getOutputTags(clientId)
      .then((outputTags) => {
        this.setState({ outputTags });
        return outputTags;
      })
      .catch((error) => {
        console.error("Error fetching output tags:", error);
      });
  };

  public componentDidUpdate(
    prevProps: Readonly<IProps>,
    prevState: Readonly<IState>
  ) {
    if (prevState.outputFilter !== this.state.outputFilter) {
      this.setState({
        outputsList: filter(this.props.outputs, (output) =>
          includes(toLower(output.Name), toLower(this.state.outputFilter))
        ),
      });
    }
    if (prevProps.outputs !== this.props.outputs) {
      this.setState({
        outputsList: filter(this.props.outputs, (output) =>
          includes(toLower(output.Name), toLower(this.state.outputFilter))
        ),
      });
    }
  }

  public render() {
    const { dimensions, dispatch } = this.props;
    const {
      isLoading,
      isEditing,
      modelInstanceId,
      editedOutput,
      clientId,
      modelId,
      outputsList,
      outputTags
    } = this.state;

    return (
      <React.Fragment>
        <AppViewTemplate
          title="Inputs & Outputs"
          tabs={
            <InputOutputEditorTabs selectedTab="outputs" dispatch={dispatch} />
          }
        >
          <MaxBound mt={3} mb={6}>
            {isLoading ? (
              <Loading />
            ) : (
              <Flex flexDirection="column" width={1} pb={100}>
                <OutputsTable
                  dispatch={dispatch}
                  outputs={outputsList}
                  onOutputEdit={this.handleEdit}
                  clientId={clientId}
                  modelId={modelId}
                  modelInstanceId={modelInstanceId}
                  handleOutputFilterChange={this.handleOutputFilterChange}
                  outputFilter={this.state.outputFilter}
                  isClientTaxonomyEnabled={this.state.isClientTaxonomyEnabled}
                  outputTags={outputTags}
                />
                <FloatingButton
                  text="Define New Output"
                  onClick={this.addOutput}
                />
                {isEditing && (
                  <EditOutputModal
                    isOpen={true}
                    isValid={this.isValidOutput}
                    onCancel={this.handleEditCancel}
                    onSave={this.handleEditSave}
                    onDelete={this.handleDelete}
                    modelInstanceId={modelInstanceId}
                    dimensions={dimensions}
                    output={editedOutput}
                    outputTags={this.state.outputTags}
                    allOutputs={this.props.outputs}
                    clients={this.props.clients}
                    clientId={clientId}
                  />
                )}
              </Flex>
            )}
          </MaxBound>
        </AppViewTemplate>
        <TabBar />
      </React.Fragment>
    );
  }

  private addOutput = (): void =>
    this.setState({ editedOutput: undefined, isEditing: true });

  private handleOutputFilterChange = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    this.setState({
      outputFilter: event.currentTarget.value,
    });
  };

  private handleEdit = (output: Output): void =>
    this.setState({ editedOutput: output, isEditing: true });

  // Surfaces a save failure to the user - most save errors here are silent
  // otherwise, since `saveOutput`'s underlying `api.post` has no app-wide
  // error-toast wiring (deliberately: adding one in the shared api client
  // would affect every API call in the app, including ~14 other
  // components/modals that already show their own success/error toasts).
  // Scoped to this one flow instead, e.g. to surface the "Output Tag
  // already mapped to another Output" conflict (CLD-3013) server-side
  // validation now returns.
  private handleSaveError = (error: unknown): void => {
    const message =
      error instanceof HTTPError && error.body?.message
        ? error.body.message
        : "Error saving output";
    Toaster.show({ message, intent: Intent.DANGER });
  };

  private handleEditSave = (output: Output): void => {
    /* Automatically create a Dimmension Aggregation Calculation Block &
    a Timescale Aggregation Calculation Block and assign their respective
    ID's to the Output before saving it */
    const { dispatch } = this.props;
    output = {
      ...output,
      TagID: output?.OutputTag?.value || null,
    };
    if (output.id) {
      dispatch(saveOutput(output))
        .then(() => this.setState({ isEditing: false }))
        .catch(this.handleSaveError);
    } else {
      const newDimensionAggregationBlock = {
        ModelInstanceID: this.state.modelInstanceId,
        Type: CalculationBlockTypes.DimensionAggregation,
      };

      const newTimeScaleAggregationBlock = {
        ModelInstanceID: this.state.modelInstanceId,
        Type: CalculationBlockTypes.TimescaleAggregation,
      };

      dispatch(saveCalculationBlock(newDimensionAggregationBlock))
        .then(
          (dimensionAggregation) =>
            (output.Dimension_Aggregation_BlockID = dimensionAggregation.id)
        )
        .then(() =>
          dispatch(saveCalculationBlock(newTimeScaleAggregationBlock))
        )
        .then((timescaleAggregation) => {
          output.Timescale_Aggregation_BlockID = timescaleAggregation.id;
          return dispatch(saveOutput(output));
        })
        .then((r) =>
          dispatch(selectOutputRow(r.payload.output.id)).then(() =>
            this.setState({ isEditing: false })
          )
        )
        .catch(this.handleSaveError);
    }
  };

  private handleEditCancel = (): void => this.setState({ isEditing: false });

  private handleDelete = (output: Output): void => {
    this.props.dispatch(deleteOutput(output)).then(() => {
      this.setState({
        editedOutput: undefined,
        isEditing: false,
      });
    });
  };

  private isValidOutput = (output: Output): boolean => {
    const { outputs } = this.props;

    if ("" === output.Name) {
      return false;
    }

    return !find(
      outputs,
      (o) => o.id !== output.id && toLower(o.Name) === toLower(output.Name)
    );
  };
}

const mapStateToProps = (state: RootState) => ({
  outputs: state.outputs,
  dimensions: state.dimensions,
  calculationBlocks: state.calculationBlocks,
  clients: state.clients,
});

export default connect(mapStateToProps)(withRouter(OutputEditorView));
